import React, { Fragment, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

const SideMenuLink = ({ label, to, icon, isChild = false, onClose, alwaysActive = false, extraIndent = false, navState }) => {
    const location = useLocation();
    const isActive = alwaysActive || location.pathname === to || location.pathname.startsWith(to + '/');

    const handleClick = () => {
        if (!isChild && onClose) {
            onClose();
        }
    };

    const paddingClass = extraIndent ? 'pl-14' : isChild ? 'pl-8' : 'p-4';

    return (
        <NavLink
            to={to}
            state={navState}
            onClick={handleClick}
            className={`flex items-center py-3 ${paddingClass} text-white hover:bg-primary-600 transition duration-300 border-l-4 ${isActive ? 'bg-primary-700 border-white' : 'border-transparent'
                }`}
        >
            {isChild ? (
                <svg className='mr-2' width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16.7074 11.7952L8.25877 5.88114C7.72855 5.50998 7 5.88931 7 6.53652V17.4635C7 18.1107 7.72855 18.49 8.25877 18.1189L16.7074 12.2048C16.8496 12.1053 16.8496 11.8947 16.7074 11.7952Z" fill="white"/>
                </svg>
            ) : typeof icon === 'string' ? (
                <img src={icon} alt={`${label} icon`} className="mr-2 w-6 h-6" />
            ) : (
                <span className="mr-2">{React.createElement(icon)}</span>
            )}
            {label}
        </NavLink>
    );
};

const SideMenu = ({ menuItems }) => {
    const location = useLocation();

    // Find which menu should be open based on current path
    const getInitialOpenMenu = () => {
        const menuWithChildren = menuItems.find(
            item => item.children && location.pathname.startsWith(item.to)
        );
        return menuWithChildren?.label || null;
    };

    const [openMenu, setOpenMenu] = useState(getInitialOpenMenu);

    const handleMenuClick = (label) => {
        // Toggle: if clicking the same menu, close it; otherwise open the new one
        setOpenMenu(openMenu === label ? null : label);
    };

    return (
        <div className="bg-primary-500 h-full w-64 pt-6">
            {menuItems.map((menuItem) => (
                <Fragment key={menuItem.label}>
                    {menuItem.children ? (
                        <div className="mb-0">
                            <button
                                onClick={() => handleMenuClick(menuItem.label)}
                                className={`flex items-center p-4 w-full text-white hover:bg-primary-600 transition duration-300 border-l-4 ${
                                    location.pathname.startsWith(menuItem.to) ? 'bg-primary-800 border-white' : 'border-transparent'
                                }`}
                            >
                                {typeof menuItem.icon === 'string' ? (
                                    <img src={menuItem.icon} alt={`${menuItem.label} icon`} className="mr-2" />
                                ) : (
                                    <span className="mr-2">{React.createElement(menuItem.icon)}</span>
                                )}
                                {menuItem.label}
                                <svg
                                    className={`ml-auto h-5 w-5 transform transition-transform ${
                                        openMenu === menuItem.label ? 'rotate-90' : ''
                                    }`}
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M6.928 5.293a1 1 0 011.414 0L10 8.586l1.658-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414 1 1 0 011.414 0L10 8.586l-1.293-1.293a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                            {openMenu === menuItem.label && (
                                <div className="pl-0">
                                    {menuItem.children.map((childItem) => (
                                        <SideMenuLink key={childItem.label} {...childItem} isChild={true} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <SideMenuLink key={menuItem.label} {...menuItem} onClose={() => setOpenMenu(null)} />
                    )}
                </Fragment>
            ))}
        </div>
    );
};

export default SideMenu;
